import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import { ToolPage, Disclaimer } from '../../components/tool-page.js'
import { EM_DASH } from '../../constants.js'
import {
    buildLeakRateTrackingXlsx,
    buildLeakRateTrackingCsv,
    buildBasicRefrigerantLogXlsx,
    buildBasicRefrigerantLogCsv,
    buildEpaComplianceWorkbookXlsx,
} from '../../util/spreadsheet-templates.js'
import {
    exportXlsx,
    exportCsv,
} from '../../util/spreadsheet.js'
import './spreadsheet-templates.css'

const TEMPLATES = [
    {
        id: 'leak-rate-tracking',
        title: 'Leak Rate Tracking',
        description:
            'Per-appliance log with auto-calculated annualized leak rate, ' +
            'EPA threshold comparison, and Compliant / EXCEEDS status. ' +
            'Fill in service dates, refrigerant added, and full charge ' +
            'and the spreadsheet computes the rest.',
        columnsNote:
            'Date, Customer, Equipment ID, Refrigerant Type, Added (lbs), ' +
            'Full Charge (lbs), Previous Service Date, Days Since Last Addition, ' +
            'Leak Rate %, EPA Threshold %, Compliance Status',
        downloadXlsx: async () => exportXlsx(buildLeakRateTrackingXlsx()),
        downloadCsv: () => exportCsv(buildLeakRateTrackingCsv()),
    },
    {
        id: 'basic-refrigerant-log',
        title: 'Basic Refrigerant Log',
        description:
            'Field-ready daily log for technicians. Record service calls, ' +
            'refrigerant quantities, and work performed. No formulas ' + EM_DASH + ' ' +
            'clean blank rows for handwritten or tablet entry.',
        columnsNote:
            'Date, Technician, EPA Cert #, Customer, Site Name, ' +
            'Equipment ID, Refrigerant Type, Added (lbs), Recovered (lbs), ' +
            'Full Charge (lbs), Work Performed, Notes',
        downloadXlsx: async () => exportXlsx(buildBasicRefrigerantLogXlsx()),
        downloadCsv: () => exportCsv(buildBasicRefrigerantLogCsv()),
    },
    {
        id: 'epa-compliance-workbook',
        title: 'Full EPA Compliance Workbook',
        description:
            '4-tab workbook covering all core recordkeeping requirements: ' +
            'service log with leak-rate formulas, repair record with ' +
            'auto-calculated 30-day deadlines, equipment register, and ' +
            'refrigerant inventory reconciliation.',
        columnsNote:
            'Tabs: Service Log, Repair Record (30-Day Deadline), ' +
            'Equipment Register, Refrigerant Inventory',
        downloadXlsx: async () =>
            exportXlsx(buildEpaComplianceWorkbookXlsx()),
        downloadCsv: null,
    },
]

export const SpreadsheetTemplates:FunctionComponent<{
    state:AppState
}> = function SpreadsheetTemplates () {
    return html`
        <${ToolPage}
            title="Spreadsheet Templates"
            description="Download ready-made refrigerant tracking spreadsheets
                with embedded formulas for leak rate and compliance status.
                No account required."
        >
            <div class="spreadsheet-templates">
                <p class="templates-intro">
                    Each template is available as .xlsx (with embedded formulas)
                    and .csv (plain text, compatible with any spreadsheet app).
                </p>

                <div class="template-list">
                    ${TEMPLATES.map(t => html`
                        <div class="template-card" key=${t.id}>
                            <h3>${t.title}</h3>
                            <p>${t.description}</p>
                            <p class="columns-note">
                                <span>Columns: </span>${t.columnsNote}
                            </p>
                            <div class="tool-actions">
                                <substrate-button
                                    type="button"
                                    onClick=${(e:Event) => {
                                        e.preventDefault()
                                        t.downloadXlsx()
                                    }}
                                >
                                    Download .xlsx
                                </substrate-button>
                                ${t.downloadCsv && html`
                                    <substrate-button
                                        type="button"
                                        onClick=${(e:Event) => {
                                            e.preventDefault()
                                            t.downloadCsv!()
                                        }}
                                    >
                                        Download .csv
                                    </substrate-button>
                                `}
                            </div>
                        </div>
                    `)}
                </div>

                <${Disclaimer} />
            </div>
        </${ToolPage}>
    `
}
